import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Bell,
  ChevronLeft,
  Link2,
  Search,
  ShieldCheck,
  Truck,
  Users2,
  Wrench,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { devicesApi } from '@/api/endpoints/devices';
import { geofencesApi } from '@/api/endpoints/geofences';
import { groupsApi } from '@/api/endpoints/groups';
import { maintenanceApi } from '@/api/endpoints/maintenance';
import { notificationsApi } from '@/api/endpoints/notifications';
import { permissionsApi } from '@/api/endpoints/permissions';
import { usersApi } from '@/api/endpoints/users';
import { translateEvent } from '@/lib/traccarLabels';
import { cn } from '@/lib/utils';
import type { Permission } from '@/api/types';
import { mensagemDeErro } from '@/lib/apiError';

type ResourceKind = 'device' | 'group' | 'geofence' | 'maintenance' | 'notification';

const RESOURCES: {
  key: ResourceKind;
  label: string;
  permissionKey: keyof Permission;
  icon: typeof Truck;
  color: string;
  bg: string;
}[] = [
  {
    key: 'device',
    label: 'Embarcações',
    permissionKey: 'deviceId',
    icon: Truck,
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
  {
    key: 'group',
    label: 'Clientes',
    permissionKey: 'groupId',
    icon: Users2,
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
  {
    key: 'geofence',
    label: 'Geocercas',
    permissionKey: 'geofenceId',
    icon: ShieldCheck,
    color: 'text-status-running',
    bg: 'bg-status-running/10',
  },
  {
    key: 'maintenance',
    label: 'Manutenções',
    permissionKey: 'maintenanceId' as keyof Permission,
    icon: Wrench,
    color: 'text-status-ready',
    bg: 'bg-status-ready/10',
  },
  {
    key: 'notification',
    label: 'Notificações',
    permissionKey: 'notificationId' as keyof Permission,
    icon: Bell,
    color: 'text-status-blocked',
    bg: 'bg-status-blocked/10',
  },
];

interface ResourceItem {
  id: number;
  name: string;
}

export function LinksPage() {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [resource, setResource] = useState<ResourceKind>('device');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Sem `all=true`: o Traccar retorna 400 sem privilégio admin/manager.
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <Link
        to="/settings"
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </Link>

      <div>
        <h1 className="text-xl font-bold">Vinculações</h1>
        <p className="text-sm text-muted-foreground">
          Vincular embarcações, clientes, geocercas, manutenções e notificações a usuários.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">Usuário</span>
            {usersQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Carregando…</p>
            ) : usersQuery.error ? (
              <p className="text-sm text-destructive">
                Erro ao carregar usuários (precisa permissão admin/manager).
              </p>
            ) : (
              <select
                value={selectedUserId ?? ''}
                onChange={(e) =>
                  setSelectedUserId(e.target.value ? Number(e.target.value) : null)
                }
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Selecione um usuário…</option>
                {(usersQuery.data ?? [])
                  .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} · {u.email}
                    </option>
                  ))}
              </select>
            )}
          </label>

          <div className="flex flex-wrap gap-1.5">
            {RESOURCES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => {
                  setResource(r.key);
                  setSearch('');
                  setError(null);
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all',
                  resource === r.key
                    ? `${r.bg} ${r.color} ring-1 ring-current`
                    : 'text-muted-foreground hover:bg-accent',
                )}
              >
                <r.icon className="h-3.5 w-3.5" />
                {r.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      {selectedUserId === null ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Link2 className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              Escolha um usuário pra ver e gerenciar os vínculos.
            </p>
          </CardContent>
        </Card>
      ) : (
        <LinkMatrix
          userId={selectedUserId}
          resource={resource}
          search={search}
          onSearchChange={setSearch}
          onError={setError}
        />
      )}
    </div>
  );
}

function LinkMatrix({
  userId,
  resource,
  search,
  onSearchChange,
  onError,
}: {
  userId: number;
  resource: ResourceKind;
  search: string;
  onSearchChange: (v: string) => void;
  onError: (msg: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const spec = RESOURCES.find((r) => r.key === resource)!;

  // Lista global do recurso (todos)
  const allQuery = useQuery({
    queryKey: ['link-resource-all', resource],
    queryFn: async (): Promise<ResourceItem[]> => {
      // Sem `all=true`: o Traccar retorna 400 sem privilégio admin/manager.
      // A lista global passa a ser apenas os recursos do usuário logado;
      // se o operador for um admin, ele segue gerindo no painel legado.
      switch (resource) {
        case 'device':
          return (await devicesApi.list()).map((d) => ({ id: d.id, name: d.name }));
        case 'group':
          return (await groupsApi.list()).map((g) => ({ id: g.id, name: g.name }));
        case 'geofence':
          return (await geofencesApi.list()).map((g) => ({ id: g.id, name: g.name }));
        case 'maintenance':
          return (await maintenanceApi.list()).map((m) => ({ id: m.id, name: m.name }));
        case 'notification':
          return (await notificationsApi.list()).map((n) => ({
            id: n.id,
            name: translateEvent(n.type),
          }));
      }
    },
  });

  // Lista vinculada ao usuário (ids)
  const linkedQuery = useQuery({
    queryKey: ['link-resource-user', resource, userId],
    queryFn: async (): Promise<number[]> => {
      switch (resource) {
        case 'device':
          return (await devicesApi.list({ userId })).map((d) => d.id);
        case 'group':
          return (await groupsApi.list({ userId })).map((g) => g.id);
        case 'geofence':
          return (await geofencesApi.list({ userId })).map((g) => g.id);
        case 'maintenance':
          return (await maintenanceApi.list({ userId })).map((m) => m.id);
        case 'notification':
          return (await notificationsApi.list({ userId })).map((n) => n.id);
      }
    },
  });

  const linkedSet = useMemo(() => new Set(linkedQuery.data ?? []), [linkedQuery.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const data = allQuery.data ?? [];
    if (!q) return data;
    return data.filter((it) => it.name.toLowerCase().includes(q));
  }, [allQuery.data, search]);

  const linkMutation = useMutation({
    mutationFn: async ({ id, link }: { id: number; link: boolean }) => {
      const payload: Permission = {
        userId,
        [spec.permissionKey]: id,
      } as Permission;
      if (link) {
        await permissionsApi.link(payload);
      } else {
        await permissionsApi.unlink(payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-resource-user', resource, userId] });
      onError(null);
    },
    onError: (e) => {
      const msg =
        mensagemDeErro(e);
      onError(msg);
    },
  });

  const toggle = (id: number) => {
    const isLinked = linkedSet.has(id);
    linkMutation.mutate({ id, link: !isLinked });
  };

  return (
    <>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={`Buscar ${spec.label.toLowerCase()}`}
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {allQuery.isLoading || linkedQuery.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando…</p>
      ) : allQuery.error ? (
        <p className="p-6 text-sm text-destructive">Erro ao carregar {spec.label.toLowerCase()}.</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Nada encontrado.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {filtered.map((it) => {
                const isLinked = linkedSet.has(it.id);
                return (
                  <li key={it.id} className="flex items-center justify-between gap-2 px-4 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className={cn(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                          spec.bg,
                          spec.color,
                        )}
                      >
                        <spec.icon className="h-4 w-4" />
                      </span>
                      <span className="truncate text-sm font-semibold">{it.name}</span>
                    </div>
                    <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold">
                      <input
                        type="checkbox"
                        checked={isLinked}
                        disabled={linkMutation.isPending}
                        onChange={() => toggle(it.id)}
                        className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
                      />
                      <span className={cn(isLinked ? 'text-primary' : 'text-muted-foreground')}>
                        {isLinked ? 'Vinculado' : 'Vincular'}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </>
  );
}

